import { Box, Text } from '@chakra-ui/react';
import type React from 'react';

import { type ColumnDefinition, ReusableTable } from '~/components/tables/ReusableTable';
import type { RevenueMap, RevenueMapEntry } from '~/types';

interface RevenueMapTableProps {
  revenueMap: RevenueMap;
  title?: string;
}

// Utility function to format currency
const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

// Utility function to format millage (in mills)
const formatMillage = (value: number): string => {
  return `${value.toFixed(2)}`;
};

// Convert RevenueMap to array format for table display
const convertToTableData = (
  revenueMap: RevenueMap
): (RevenueMapEntry & { [key: string]: unknown })[] => {
  return Object.values(revenueMap)
    .sort((a, b) => a.taxYear - b.taxYear)
    .map((entry) => ({
      ...entry,
      // Add index signature to satisfy type constraint
      [Symbol.toStringTag]: 'RevenueMapEntry',
    }));
};

export const RevenueMapTable: React.FC<RevenueMapTableProps> = ({ revenueMap }) => {
  // Convert to table data
  const tableData = convertToTableData(revenueMap);

  // Define column structure
  const columns: ColumnDefinition<RevenueMapEntry & { [key: string]: unknown }>[] = [
    {
      key: 'taxYear',
      header: 'Tax Year',
      accessor: 'taxYear',
      headerTextAlign: 'center',
      cellTextAlign: 'center',
    },
    {
      key: 'millage',
      header: 'Millage',
      accessor: (item) => formatMillage(item.millage),
      headerTextAlign: 'right',
      cellTextAlign: 'right',
    },
    {
      key: 'taxableValue',
      header: 'Taxable Value',
      accessor: (item) => formatCurrency(item.taxableValue),
      headerTextAlign: 'right',
      cellTextAlign: 'right',
    },
    {
      key: 'revenue',
      header: 'Revenue',
      accessor: (item) => formatCurrency(item.revenue),
      headerTextAlign: 'right',
      cellTextAlign: 'right',
    },
    {
      key: 'existingDebt',
      header: 'Existing Debt',
      accessor: (item) => formatCurrency(item.existingDebt),
      headerTextAlign: 'right',
      cellTextAlign: 'right',
    },
    {
      key: 'availableRevenue',
      header: 'Available Revenue',
      accessor: (item) => formatCurrency(item.availableRevenue),
      headerTextAlign: 'right',
      cellTextAlign: 'right',
    },
    {
      key: 'revenueCapacity',
      header: 'Revenue Capacity',
      accessor: (item) => formatCurrency(item.revenueCapacity),
      headerTextAlign: 'right',
      cellTextAlign: 'right',
    },
    {
      key: 'isTunable',
      header: 'Tunable',
      accessor: (item) => (item.isTunable ? 'Yes' : 'No'),
      headerTextAlign: 'center',
      cellTextAlign: 'center',
    },
  ];

  // Handle empty data
  if (!revenueMap || Object.keys(revenueMap).length === 0) {
    return (
      <Box>
        <Text color="gray.500">No revenue map data available</Text>
      </Box>
    );
  }

  return (
    <Box>
      {/* Data table */}
      <ReusableTable columns={columns} data={tableData} />

      {/* Footer information */}
      <Text mt={2} fontSize="sm" color="gray.600">
        {tableData.length} year{tableData.length !== 1 ? 's' : ''} in revenue projection
      </Text>
    </Box>
  );
};
